import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Car, ChevronRight, Tag, Clock, History } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { wallpaperBgStyle, hasWallpaper, cardSurfaceClass } from '../../components/shared/vehiclePresentation';
import { VehicleImg } from '../../components/shared/VehicleImg';
import { vehicleActivityAPI } from '../../lib/api';
import { fmtIncentive, fmtDate, AUTO_EVENT_LABELS } from './specialsUtil';
import SpecialActionBar from './SpecialActionBar';
import VehicleManageSection from './VehicleManageSection';

const ts = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

/**
 * Shared Automotive vehicle detail dialog for the SHARED Command Center.
 * Reused by the Inventory panel AND the Rebate Alert list so both surfaces
 * render the SAME vehicle context — Inventory-Specials block (Phase D) + the
 * manager Authorize / Authorize & Change / Decline controls + audit (Phase E).
 */
export default function VehicleDetailDialog({ detail, onClose, onOpenConvo, onActionDone }) {
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || 'en').slice(0, 2);
    const [d, setD] = useState(detail);
    useEffect(() => { setD(detail); }, [detail]);

    const v = d?.vehicle;
    const sp = d?.specials || {};
    const hasSpecials = (sp.eligible?.length || sp.authorized?.length || sp.declined?.length || sp.approaching?.length);

    // Self-refresh after a manager action, then notify the parent list/count.
    const refresh = async () => {
        try { const r = await vehicleActivityAPI.vehicleDetail(v.id); setD(r.data); } catch { /* noop */ }
        await onActionDone?.();
    };

    const auditLabel = (e) => t(`rebateAlert.audit.${e.type}`, e.type);

    return (
        <Dialog open={!!detail} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto bg-slate-950 border-slate-800 text-slate-100" data-testid="cc-vehicle-detail">
                <DialogHeader><DialogTitle className="text-white">{v && `${v.year} ${v.make} ${v.model} · #${v.stock_number}`}</DialogTitle></DialogHeader>
                {v?.photos?.[0] ? (
                    hasWallpaper(v) ? (
                        <div className="w-full h-40 rounded-lg border border-slate-800 flex items-center justify-center overflow-hidden" style={wallpaperBgStyle(v) || undefined} data-testid="cc-vehicle-photo">
                            <div className={`m-2 rounded-md overflow-hidden ${cardSurfaceClass(v.card_style, 'dark')} p-1`}><VehicleImg src={v.photos[0]} alt="" imgClassName="max-h-32 w-auto object-contain rounded" placeholderClassName="w-full h-32" label={t('commandCenter.automotive.imageUnavailable', 'Image unavailable')} /></div>
                        </div>
                    ) : (
                        <VehicleImg src={v.photos[0]} alt="" imgClassName="w-full h-40 object-cover rounded-lg border border-slate-800" placeholderClassName="w-full h-40 rounded-lg border border-slate-800" label={t('commandCenter.automotive.imageUnavailable', 'Image unavailable')} />
                    )
                ) : (
                    <div className="w-full h-40 rounded-lg border border-slate-800 flex flex-col items-center justify-center gap-1 text-slate-400 bg-slate-900/40" data-testid="cc-vehicle-photo"><Car className="w-8 h-8 opacity-60" /><span className="text-[10px] opacity-70">{t('commandCenter.automotive.imageUnavailable', 'Image unavailable')}</span></div>
                )}
                {d && (
                    <div className="space-y-4">
                        {/* Days in inventory */}
                        {sp && (
                            <div className="flex items-center gap-2 text-xs text-slate-400" data-testid="cc-detail-daysininv">
                                <Clock className="w-3.5 h-3.5" />
                                {t('rebateAlert.daysInInventory', { n: daysIn(v) })} · {fmtDate(v?.created_at)}
                            </div>
                        )}

                        {/* ── Inventory Special block (Phase D) ── */}
                        <div className="rounded-xl border border-amber-600/30 bg-amber-500/5 p-3" data-testid="cc-detail-specials">
                            <p className="text-xs font-bold uppercase tracking-wide text-amber-400 flex items-center gap-1.5 mb-2">
                                <Tag className="w-3.5 h-3.5" />{t('rebateAlert.detailTitle')}
                            </p>
                            {!hasSpecials ? (
                                <p className="text-sm text-slate-400" data-testid="cc-detail-nospecial">{t('rebateAlert.noSpecial')}</p>
                            ) : (
                                <div className="space-y-2">
                                    {(sp.authorized || []).map((i) => (
                                        <div key={i.id} data-testid={`cc-special-authorized-wrap-${i.id}`}>
                                            <SpecialRow tag={t('rebateAlert.state.authorized')} tagCls="text-emerald-400"
                                                incentive={fmtIncentive(i.authorized_incentive || i.original_incentive, t, lang)}
                                                label={t('rebateAlert.currentSpecial')} threshold={i.threshold_days}
                                                when={t('rebateAlert.eligibleOn', { date: fmtDate(i.eligibility_date) })} t={t} testid="cc-special-authorized" />
                                            <SpecialActionBar instance={i} authorized onDone={async () => { await refresh(); await onActionDone?.(); }} />
                                        </div>
                                    ))}
                                    {(sp.eligible || []).map((i) => (
                                        <div key={i.id} data-testid={`cc-special-eligible-wrap-${i.id}`}>
                                            <SpecialRow tag={t('rebateAlert.state.eligible')} tagCls="text-amber-400"
                                                incentive={fmtIncentive(i.original_incentive, t, lang)}
                                                label={t('rebateAlert.proposedSpecial')} threshold={i.threshold_days}
                                                when={t('rebateAlert.awaitingAuth')} t={t} testid="cc-special-eligible" />
                                            <SpecialActionBar instance={i} onDone={async () => { await refresh(); await onActionDone?.(); }} />
                                        </div>
                                    ))}
                                    {(sp.declined || []).map((i) => (
                                        <SpecialRow key={i.id} tag={t('rebateAlert.state.declined', t('rebateAlert.audit.declined'))} tagCls="text-rose-400"
                                            incentive={fmtIncentive(i.original_incentive, t, lang)}
                                            label={t('rebateAlert.proposedSpecial')} threshold={i.threshold_days}
                                            when={fmtDate(i.declined_at)} t={t} testid="cc-special-declined" />
                                    ))}
                                    {(sp.approaching || []).map((a, idx) => (
                                        <SpecialRow key={`ap-${idx}`} tag={t('rebateAlert.state.approaching')} tagCls="text-sky-400"
                                            incentive={fmtIncentive(a.proposed_incentive, t, lang)}
                                            label={t('rebateAlert.proposedSpecial')} threshold={a.threshold_days}
                                            when={t('rebateAlert.approachingIn', { count: a.days_until })} t={t} testid="cc-special-approaching" />
                                    ))}
                                </div>
                            )}
                            {/* Notification history */}
                            <p className="text-[11px] font-semibold text-slate-500 mt-3 mb-1">{t('rebateAlert.notificationHistory')}</p>
                            {(sp.notifications || []).length === 0 ? (
                                <p className="text-[11px] text-slate-500" data-testid="cc-detail-notif-empty">{t('rebateAlert.notifNever')}</p>
                            ) : (
                                <ul className="space-y-0.5">
                                    {(sp.notifications || []).slice(0, 8).map((n) => (
                                        <li key={n.id} className="text-[11px] text-slate-400 flex items-center justify-between gap-2" data-testid="cc-detail-notif-row">
                                            <span>{n.rule_type}</span><span className="text-slate-500">{ts(n.created_at)}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            {/* ── Audit trail (Phase E) — append-only, time-sorted ── */}
                            <p className="text-[11px] font-semibold text-slate-500 mt-3 mb-1 flex items-center gap-1"><History className="w-3 h-3" />{t('rebateAlert.auditTitle')}</p>
                            <ul className="space-y-0.5" data-testid="cc-detail-audit">
                                {(sp.audit || []).map((e, idx) => (
                                    <li key={idx} className="text-[11px] text-slate-400 flex items-start justify-between gap-2" data-testid="cc-audit-row">
                                        <span className="flex-1">
                                            {auditLabel(e)}
                                            {e.type === 'authorized' && e.changed && (
                                                <span className="text-amber-400"> · {t('rebateAlert.auditChanged', { from: fmtIncentive(e.original_incentive, t, lang), to: fmtIncentive(e.authorized_incentive, t, lang) })}</span>
                                            )}
                                            {e.type === 'authorized' && e.authorized_early && (
                                                <span className="text-amber-400"> · {t('rebateAlert.authorizedEarly')}</span>
                                            )}
                                            {e.type === 'notified' && e.rule_type ? ` · ${e.rule_type}` : ''}
                                        </span>
                                        <span className="text-slate-500 shrink-0">{ts(e.at)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {d.conversations?.length > 0 && (
                            <div>
                                <p className="text-xs font-semibold text-slate-400 mb-1">{t('commandCenter.automotive.conversations', 'Conversations')}</p>
                                {d.conversations.map((c) => (
                                    <button key={c.id} onClick={() => onOpenConvo?.(c.id)} className="w-full text-left text-sm p-2 rounded bg-slate-800/50 hover:bg-slate-800 mb-1" data-testid="cc-detail-convo">
                                        {c.customer?.name} — {c.status} <ChevronRight className="w-3.5 h-3.5 inline text-slate-400" />
                                    </button>
                                ))}
                            </div>
                        )}
                        <div>
                            <p className="text-xs font-semibold text-slate-400 mb-1">{t('commandCenter.automotive.timeline', 'Timeline')}</p>
                            {(d.events || []).map((e) => (
                                <div key={e.id} className="flex items-center gap-2 text-sm py-1 border-b border-slate-800">
                                    <span className="text-slate-300 flex-1">{AUTO_EVENT_LABELS[e.event_type] || e.event_type}</span>
                                    <span className="text-slate-400 text-xs">{ts(e.created_at)}</span>
                                </div>
                            ))}
                        </div>

                        <VehicleManageSection vehicle={v} card={d.card} onSaved={refresh} />
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function daysIn(v) {
    if (!v?.created_at) return 0;
    const d = new Date(v.created_at).getTime();
    return d ? Math.max(0, Math.floor((Date.now() - d) / 86400000)) : 0;
}

function SpecialRow({ tag, tagCls, incentive, label, threshold, when, t, testid }) {
    return (
        <div className="flex items-start gap-2 text-sm" data-testid={testid}>
            <span className={`text-[10px] font-bold uppercase tracking-wide ${tagCls} shrink-0 mt-0.5`}>{tag}</span>
            <div className="flex-1 min-w-0">
                <div className="text-slate-100 font-semibold">{incentive} <span className="text-slate-400 font-normal">· {label}</span></div>
                <div className="text-[11px] text-slate-400">{t('rebateAlert.thresholdDays', { n: threshold })} · {when}</div>
            </div>
        </div>
    );
}
